import { styled } from '@mui/material/styles';
import { Box, Typography, Button, List, ListItem } from '@mui/material';

export const WillContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(4),
  fontFamily: '"Times New Roman", Times, serif', // Classic document font
  color: theme.palette.text.primary,
  maxWidth: '800px', // Standard document width
  margin: '0 auto',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[3],

  '@media print': {
    boxShadow: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    maxWidth: '100%',
    backgroundColor: 'transparent',
    color: '#000', // Ensure black text for printing
  },
}));

export const WillHeader = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  fontWeight: 'bold',
  marginBottom: theme.spacing(3),
  textTransform: 'uppercase',

  '@media print': {
    fontSize: '1.8rem', // Adjust print size if needed
  },
}));

export const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1.5),
  textTransform: 'uppercase',

  '@media print': {
    fontSize: '1.1rem',
  },
}));

export const Paragraph = styled(Typography)(({ theme }) => ({
  textAlign: 'justify',
  marginBottom: theme.spacing(1.5),
  lineHeight: 1.6,

  '@media print': {
     fontSize: '12pt', // Standard print font size
     marginBottom: theme.spacing(1),
  },
}));

export const IndentedParagraph = styled(Paragraph)(({ theme }) => ({
  marginLeft: theme.spacing(4),

   '@media print': {
     marginLeft: '40px', // Adjust print indent
   },
}));

export const StyledList = styled(List)(({ theme }) => ({
  marginLeft: theme.spacing(4),
  paddingLeft: theme.spacing(2),
   listStyleType: 'disc',

   '@media print': {
     marginLeft: '40px',
     paddingLeft: '20px',
   },
}));

export const StyledListItem = styled(ListItem)(({ theme }) => ({
  display: 'list-item',
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),

  '@media print': {
     fontSize: '12pt',
     paddingTop: '2px',
     paddingBottom: '2px',
  },
}));


export const SignatureBlockContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  paddingTop: theme.spacing(2),
  width: '100%',

  '@media print': {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    marginTop: 0,
    marginBottom: 0,
    paddingTop: 0,
    pageBreakInside: 'avoid',
    pageBreakBefore: 'always',
  },
}));

export const SignatureContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end', // Align barcode and text to bottom
  justifyContent: 'space-between', // Space out barcode and lines
  gap: theme.spacing(4),
  borderTop: `1px dashed ${theme.palette.divider}`, // Visual separator
  paddingTop: theme.spacing(2),

  '@media print': {
     borderTop: '1px dashed #ccc',
     paddingTop: '15px',
     gap: '30px',
  }
}));

export const BarcodeContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: '150px', // Ensure some minimum width
}));

export const BarcodeValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  letterSpacing: '1px',
  marginBottom: theme.spacing(0.5),

  '@media print': {
    fontSize: '8pt',
  },
}));

export const BarcodeLines = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  height: '35px', // Fixed height for barcode area
  overflow: 'hidden',
});

export const BarcodeLine = styled(Box)(({ theme }) => ({
  width: '2px',
  backgroundColor: theme.palette.text.primary,
  margin: '0 1px',

  '@media print': {
    backgroundColor: '#000',
     width: '1.5px',
     margin: '0 0.5px',
  }
}));

export const SignatureLinesContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1, // Take remaining space
  textAlign: 'left',
  '& p': {
      margin: theme.spacing(0.5, 0),
      fontSize: '0.9rem',
      lineHeight: 1.8, // Space out lines for signing
      '@media print': {
         fontSize: '10pt',
         lineHeight: 2.0,
         margin: '4px 0',
      }
  },
   '& hr': {
     display: 'none', // Hide the <hr> now, using CSS for lines
   }
}));


export const PageBreak = styled('div')({
  pageBreakAfter: 'always',
  height: 0,
  visibility: 'hidden', // Hide element itself, only use for page break

  '@media screen': { // Don't break page on screen
    pageBreakAfter: 'unset',
  }
});

export const ResultPageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',

  '@media print': {
     padding: 0,
     margin: 0,
     textAlign: 'left',
     '& > h1': { // Hide the outer H1 on print
        display: 'none',
     }
  },
}));


export const ActionButtonGroup = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),

  '@media print': {
    display: 'none', // Hide buttons on print
  }
}));

export const BackButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(1.5, 2.5),
  fontWeight: 600,
  background: theme.palette.primary.main,
}));